import DOMPurify, { type Config } from "dompurify";

/**
 * 남이 보낸 HTML을 화면에 띄우기 전에 정화한다.
 *
 * 방어는 세 겹이다. 하나라도 뚫리면 나머지가 막는다.
 *   1. 여기서 DOMPurify로 스크립트·폼·외부 리소스를 걷어낸다
 *   2. 결과를 scripts 없는 sandbox iframe에 넣는다 (HtmlMailViewer)
 *   3. iframe 안에 CSP `default-src 'none'`을 박아 네트워크를 끊는다
 *
 * 원격 이미지를 기본으로 막는 건 XSS 때문이 아니라 **추적 픽셀** 때문이다.
 * 1×1 이미지 하나로 "언제 열었는지, 어느 IP인지"가 발신자에게 넘어간다.
 */

export type SanitizedMail = {
  html: string;
  /** 막아둔 원격 리소스 수. 0이면 "이미지 불러오기" 버튼을 띄울 필요가 없다. */
  blockedCount: number;
};

/** cid: 참조를 채워 넣을 표. 키는 소문자 Content-ID (lib/inlineImages.ts) */
export type InlineImages = Record<string, string>;

/** `cid:logo@example` 또는 `cid:<logo@example>` 에서 알맹이만 꺼낸다. */
function contentIdOf(src: string): string | null {
  const trimmed = src.trim();
  if (!/^cid:/i.test(trimmed)) return null;

  const bare = trimmed
    .slice(4)
    .replace(/^</, "")
    .replace(/>$/, "")
    .trim()
    .toLowerCase();
  return bare.length > 0 ? bare : null;
}

const PURIFY_CONFIG: Config = {
  // 메일 본문에 있을 이유가 없는 것들. DOMPurify 기본값에 더해 못을 박는다.
  FORBID_TAGS: [
    "script",
    "iframe",
    "frame",
    "frameset",
    "object",
    "embed",
    "applet",
    "form",
    "input",
    "button",
    "textarea",
    "select",
    "option",
    "link",
    "meta",
    "base",
    "template",
    "noscript",
  ],
  FORBID_ATTR: ["srcset", "ping", "formaction", "form", "autofocus"],
  ALLOW_DATA_ATTR: false,
  // SVG/MathML은 mXSS 표면이 넓고 메일에서 거의 쓰이지 않는다
  USE_PROFILES: { html: true },
};

/** 정화한 뒤 남아도 되는 URL 스킴 */
function isSafeHref(value: string): boolean {
  const trimmed = value.trim().toLowerCase();
  return (
    trimmed.startsWith("https://") ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("mailto:") ||
    trimmed.startsWith("tel:") ||
    trimmed.startsWith("#")
  );
}

/** 인라인 첨부(cid:)와 data: 는 밖으로 나가지 않으므로 막지 않는다 */
function isLocalResource(value: string): boolean {
  const trimmed = value.trim().toLowerCase();
  return trimmed.startsWith("data:") || trimmed.startsWith("cid:");
}

export function sanitizeMailHtml(
  raw: string,
  allowRemoteImages: boolean,
  /** 같은 메일에 첨부돼 본문에 박힌 이미지들. 없으면 그 자리는 빈 칸으로 둔다. */
  inlineImages: InlineImages = {},
): SanitizedMail {
  const clean = DOMPurify.sanitize(raw, PURIFY_CONFIG);
  const doc = new DOMParser().parseFromString(clean, "text/html");

  let blockedCount = 0;

  for (const image of doc.querySelectorAll("img")) {
    const src = image.getAttribute("src");
    if (!src) continue;

    // 본문에 박힌 첨부. 실제 파일로 바꿔 끼운다 — 네트워크로 나가지 않으므로
    // "이미지 불러오기"를 누르지 않아도 보여준다.
    const contentId = contentIdOf(src);
    if (contentId !== null) {
      const resolved = inlineImages[contentId];
      if (resolved) {
        image.setAttribute("src", resolved);
      } else {
        // 첨부를 못 받았다. cid:를 그대로 두면 브라우저가 깨진 아이콘을 그린다.
        image.removeAttribute("src");
        image.setAttribute("alt", image.getAttribute("alt") ?? "이미지");
      }
      continue;
    }

    if (isLocalResource(src)) continue;

    if (allowRemoteImages) continue;

    // 나중에 되돌릴 수 있게 원래 주소는 남겨둔다
    image.setAttribute("data-blocked-src", src);
    image.removeAttribute("src");
    image.setAttribute("alt", image.getAttribute("alt") ?? "이미지");
    blockedCount += 1;
  }

  // background="..." 같은 옛 메일 문법으로도 요청이 나간다
  for (const element of doc.querySelectorAll("[background]")) {
    const value = element.getAttribute("background");
    if (!value || isLocalResource(value)) continue;

    if (!allowRemoteImages) {
      element.removeAttribute("background");
      blockedCount += 1;
    }
  }

  // style="background-image:url(...)" 도 마찬가지다
  if (!allowRemoteImages) {
    for (const element of doc.querySelectorAll("[style]")) {
      const style = element.getAttribute("style") ?? "";
      if (!/url\s*\(/i.test(style)) continue;

      const stripped = style
        .split(";")
        .filter((declaration) => !/url\s*\(/i.test(declaration))
        .join(";");

      element.setAttribute("style", stripped);
      blockedCount += 1;
    }
  }

  for (const anchor of doc.querySelectorAll("a")) {
    const href = anchor.getAttribute("href");

    if (!href || !isSafeHref(href)) {
      anchor.removeAttribute("href");
      continue;
    }

    // 메일 안 링크는 항상 새 창으로. opener를 끊어 탭 내빙을 막는다.
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer nofollow");
  }

  return { html: doc.body.innerHTML, blockedCount };
}

/**
 * iframe에 넣을 완전한 문서를 만든다.
 * CSP를 문서 안에 박아서, 위 정화를 통과한 무언가가 남아 있어도 밖으로
 * 요청을 보내지 못하게 한다.
 */
export function buildMailDocument(
  bodyHtml: string,
  allowRemoteImages: boolean,
): string {
  const imgSrc = allowRemoteImages ? "https: data:" : "data:";
  const csp = [
    "default-src 'none'",
    `img-src ${imgSrc}`,
    "style-src 'unsafe-inline'",
    "font-src data:",
  ].join("; ");

  return `<!doctype html>
<html lang="ko">
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${csp}">
<style>
  html, body { margin: 0; padding: 0; }
  body {
    font-family: -apple-system, "Apple SD Gothic Neo", "Malgun Gothic", sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #111;
    word-break: break-word;
  }
  img { max-width: 100%; height: auto; }
  /* 막힌 이미지 자리를 회색 상자로 남겨 레이아웃이 무너지지 않게 한다 */
  img[data-blocked-src] {
    min-width: 24px;
    min-height: 24px;
    background: #e8e8e8;
    border: 1px dashed #c4c4c4;
  }
  table { max-width: 100%; }
</style>
</head>
<body>${bodyHtml}</body>
</html>`;
}
